// src/shared/utils/selectionsSupport.js

import {
  hasUrlParameter,
  getAllUrlParameterValues,
  getUrlParameter,
} from "./utils";
import parseStringData from "./parseStringData";
import { GetBookmark, GetBookmarks, ApplyBookmark } from "../qlik-api/qlikUtils";
import stripQueryParamsFromUrl from "./stripQueryParamsFromUrl"; // ensure this exists

// Robust numeric detection (integers/decimals, optional sign)
const isNumericLike = (v) => {
  if (typeof v === "number" && Number.isFinite(v)) return true;
  if (typeof v !== "string") return false;
  const s = v.trim();
  return /^[+-]?\d+(\.\d+)?$/.test(s);
};

// Read the saved mashup route (page) from a bookmark's properties
async function getBookmarkSavedPage(app, bookmarkId) {
  // Preferred: via enigma model
  try {
    const bmModel =
      app?.model?.enigmaModel?.getBookmark &&
      (await app.model.enigmaModel.getBookmark(bookmarkId));
    if (bmModel?.getProperties) {
      const props = await bmModel.getProperties();
      if (props && typeof props.page === "string") return props.page;
    }
  } catch (e) {
    console.warn("[selectionsSupport] getBookmark (engine) failed:", e);
  }

  // Fallback: via helper (shape may vary)
  try {
    const b = await GetBookmark(app, bookmarkId);
    if (b?.getProperties) {
      const props = await b.getProperties();
      if (props && typeof props.page === "string") return props.page;
    }
    const props = b?.qBookmark || b?.properties || b;
    const page = props?.page ?? props?.qData?.page ?? null;
    if (typeof page === "string") return page;
  } catch (e) {
    console.warn("[selectionsSupport] GetBookmark fallback failed:", e);
  }

  return null;
}

export default async function applyURLSelections(app, navigate) {
  if (!app) return;

  // ----------------------------------------------------
  // 1. Bookmark handling (only if ?bookmark= in URL)
  //    Apply and navigate to the saved page, then strip the param.
  // ----------------------------------------------------
  if (hasUrlParameter("bookmark")) {
    const bmIds = getAllUrlParameterValues("bookmark");
    let navigatedFromBookmark = false;

    for (const id of bmIds) {
      try {
        await ApplyBookmark(app, id);
        console.log(`Applied bookmark ${id}`);

        // Navigate to the page saved with the bookmark (align with menu behavior)
        if (!navigatedFromBookmark && typeof navigate === "function") {
          const page = await getBookmarkSavedPage(app, id);
          if (page) {
            const target = page.startsWith("/") ? page : `/${page}`;
            navigate(target);
            navigatedFromBookmark = true;
          }
        }
      } catch (e) {
        console.error(`Failed to apply bookmark ${id}`, e);
      }
    }

    // Clean URL so it doesn't re-trigger
    stripQueryParamsFromUrl(["bookmark"]);
  }

  // ----------------------------------------------------
  // 2. “select” param (robust parsing)
  //    Accepts patterns like:
  //    ?select=Field/["A","B"]
  //    ?select=Field/["25449103"]/
  //    ?select=Field/[123,456]
  //    For numeric-only values, send BOTH qText and qNumber variants in one call.
  // ----------------------------------------------------
  if (hasUrlParameter("select")) {
    const selects = getAllUrlParameterValues("select");
    let appliedAnySelect = false;

    for (const sel of selects) {
      // Split only on the FIRST slash (field / payload)
      const slashIdx = sel.indexOf("/");
      if (slashIdx === -1) continue;

      // Decode field and raw payload
      const fieldEnc = sel.slice(0, slashIdx);
      const rawPartEnc = sel.slice(slashIdx + 1);

      const field = decodeURIComponent(fieldEnc).replace(/\+/g, " ").trim();

      // Decode payload, strip any trailing slashes that often appear before '#'
      let raw = decodeURIComponent(rawPartEnc).trim().replace(/\/+$/, "");

      if (!field || !raw) continue;

      let vals = [];
      let parsedFromJson = false;

      // Primary: try JSON (e.g., ["A","B"] or [1,2])
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          vals = parsed;
        } else if (parsed != null) {
          vals = [parsed];
        }
        parsedFromJson = true;
      } catch {
        // Fallback: strip [ ] and parse comma-separated
        const inner = raw.replace(/^\[|\]$/g, "");
        vals =
          inner === ""
            ? []
            : inner
                .split(",")
                .map((s) => s.trim().replace(/^["']|["']$/g, ""));
      }

      // Build combined qValues:
      // - Always include qText(String(v)) so text fields & leading zeros match.
      // - If numeric-like (or JSON gave a number), ALSO include qNumber(Number(v)).
      // - De-duplicate entries.
      const seen = new Set();
      const qValues = [];
      const pushUnique = (obj) => {
        const key = obj.qIsNumeric ? `N:${obj.qNumber}` : `T:${obj.qText}`;
        if (!seen.has(key)) {
          seen.add(key);
          qValues.push(obj);
        }
      };

      for (let v of vals) {
        // Always include text variant
        pushUnique({ qText: String(v) });

        // Add numeric variant when appropriate
        if (parsedFromJson ? typeof v === "number" : isNumericLike(v)) {
          const n = typeof v === "number" ? v : Number(String(v).trim());
          if (Number.isFinite(n)) {
            pushUnique({ qNumber: n, qIsNumeric: true });
          }
        }
      }

      console.log("[URL select] field:", field, "values:", vals);

      if (qValues.length === 0) continue;

      try {
        await Promise.resolve(
          app.field(field).selectValues(
            qValues,
            false, // DO NOT toggle; make idempotent
            false
          )
        );
        appliedAnySelect = true;
      } catch (e) {
        console.error(`Failed to apply select on ${field}`, e);
      }
    }

    if (appliedAnySelect) {
      stripQueryParamsFromUrl(["select"]);
    }
  }

  // ----------------------------------------------------
  // 3. Optional: ?sheet=sheetId → navigate
  // ----------------------------------------------------
  if (hasUrlParameter("sheet")) {
    const sheet = getUrlParameter("sheet");
    navigate(`/sheet/${sheet}`);
    // Optionally also strip:
    // stripQueryParamsFromUrl(["sheet"]);
  }
}
